import Table from '@tiptap/extension-table';

import { MarkdownNodeSpec } from 'tiptap-markdown';
import HTMLNode from './html';
import { childNodes } from '../utils/prosemirror';
import { Node as ProseNode, DOMParser as ProseMirrorDOMParser } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';

export default Table.extend({
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste: (view, event) => {
            // Extract the HTML content from the clipboard
            const html = event.clipboardData?.getData('text/html');
            if (html) {
              const div = document.createElement('div');
              div.innerHTML = html;
              const table = div.querySelector('table');

              if (table) {
                // don’t create nested tables
                if (this.editor.isActive(this.type.name)) {
                  return true;
                }
                table.querySelectorAll('td, th').forEach(cell => {
                  cell.removeAttribute('colspan');
                  cell.removeAttribute('rowspan');
                });

                // Make the first row a header by converting <td> to <th>
                const firstRow = table.querySelector('tr');
                if (firstRow) {
                  firstRow.querySelectorAll('td').forEach(td => {
                    const th = document.createElement('th');
                    th.innerHTML = td.innerHTML;
                    td.replaceWith(th);
                  });
                }

                // Convert the modified table back to ProseMirror-compatible nodes
                const parser = ProseMirrorDOMParser.fromSchema(view.state.schema);
                const tableNode = parser.parse(div);

                // Insert the table node into the document at the current selection
                const transaction = view.state.tr.replaceSelectionWith(tableNode);
                view.dispatch(transaction);

                return true; // Indicate that the paste has been handled
              }
            }

            return false; // Allow default paste behavior if not handled
          },
        },
      }),
    ];
  },
  addStorage(): { markdown: MarkdownNodeSpec } {
    return {
      markdown: {
        serialize(state, node, parent) {
          if (!isMarkdownSerializable(node)) {
            HTMLNode.storage.markdown.serialize.call(this, state, node, parent, 0); // last argument is ignored by our implementation
            return;
          }
          // @ts-ignore
          state.inTable = true;
          node.forEach((row, _p, i) => {
            state.write('| ');
            row.forEach((col, _p, j) => {
              if (j) {
                state.write(' | ');
              }
              const cellContent = col.firstChild;
              if (cellContent?.textContent.trim()) {
                state.renderInline(cellContent);
              }
            });
            state.write(' |');
            state.ensureNewLine();
            if (!i) {
              const delimiterRow = Array.from({ length: row.childCount })
                .map(() => '---')
                .join(' | ');
              state.write(`| ${delimiterRow} |`);
              state.ensureNewLine();
            }
          });
          state.closeBlock(node);
          // @ts-ignore
          state.inTable = false;
        },
        parse: {
          // handled by markdown-it
        },
      },
    };
  },
});

function hasSpan(node: ProseNode) {
  return node.attrs.colspan > 1 || node.attrs.rowspan > 1;
}

function isMarkdownSerializable(node: ProseNode) {
  const rows = childNodes(node);
  const firstRow = rows[0];
  const bodyRows = rows.slice(1);

  if (childNodes(firstRow).some(cell => hasSpan(cell) || cell.childCount > 1)) {
    return false;
  }

  if (bodyRows.some(row => childNodes(row).some(cell => hasSpan(cell) || cell.childCount > 1))) {
    return false;
  }

  return true;
}
